import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  compact,
  difference,
  differenceWith,
  find,
  isEmpty,
  isEqual,
  join,
  keys,
  map,
  debounce,
  includes,
  reject,
  flatMap,
} from 'lodash';
import './CommercialsModal.scss';
import ModalWithConfirmationButtons from '../../../../../common/Modal/ModalWithConfirmationButtons/ModalWithConfirmationButtons';
import Dropdown from '../../../../../common/Dropdown/Dropdown/Dropdown';
import WindowEventListener from '../../../../../common/WindowEventListener/WindowEventListener';
import TvCustomTimeframe from '../../TvCustomTimeframe/TvCustomTimeframe';
import SelectToggle from '../../../../../common/SelectToggle/SelectToggle';
import ClickableIcon from '../../../../../common/ClickableIcon/ClickableIcon';
import {
  dateToString,
  isSmartTvChannel,
  LEVEL_OF_EXPOSURE_COMMERCIALS_TOOLTIP,
  LEVELS_OF_EXPOSURE,
  VIEWERSHIP_MODES,
  VIEWING_TYPES,
  VIEWING_TYPES_MAP,
  CHANNELS,
  isFranceGeo,
} from '../../../../../../../data/audience-segment-builder-helper';
import SingleSelectToggle from '../../../../../common/SingleSelectToggle/SingleSelectToggle';
import { getKeywords } from '../../../../../../services/TvService';
import ResultsTable from '../../../../../common/ResultsTable/ResultsTable';
import { getTvCommercialsMetaDataV2 } from '../../../../../../services/AudienceInsightsService';
import DropdownWithHeader from '../../../../../common/Dropdown/DropdownWithHeader/DropdownWithHeader';

const creativeVideoLinkBuilder = (creative) =>
  `http://customportal.kantarmediana.com/Creatives/Gateway.aspx?cid=${creative.value}&mtid=1&uid=20180529`;

const MAX_SELECTED_MAP = {
  products: 100,
  categories: 7,
  'parent brands': 3,
  keywords: 7,
};

const createLimitMsg = (level) => 'Sorry, this field is limited to ' + MAX_SELECTED_MAP[level] + ' ' + level;

const ResponseAttributesEnum = {
  CATEGORY: 'categories',
  BRAND_PARENT: 'brand_parents',
  BRAND: 'brands',
  PRODUCT: 'products',
  CREATIVE: 'creatives',
};

const ExpectedResponseAttributes = {
  [CHANNELS.smart_tv_inscape?.name]: {
    initNew: [ResponseAttributesEnum.BRAND],
    fetchByBrand: [ResponseAttributesEnum.CREATIVE],
  },
  [CHANNELS.hisense?.name]: {
    initNew: [ResponseAttributesEnum.CATEGORY, ResponseAttributesEnum.BRAND, ResponseAttributesEnum.PRODUCT],
    fetchByCategory: [ResponseAttributesEnum.BRAND, ResponseAttributesEnum.PRODUCT],
    fetchByBrand: [ResponseAttributesEnum.PRODUCT, ResponseAttributesEnum.CREATIVE],
    fetchByProduct: [ResponseAttributesEnum.CREATIVE],
  },
  [CHANNELS.tivo?.name]: {
    initNew: [ResponseAttributesEnum.BRAND_PARENT, ResponseAttributesEnum.BRAND, ResponseAttributesEnum.PRODUCT],
    fetchByParent: [ResponseAttributesEnum.BRAND, ResponseAttributesEnum.PRODUCT, ResponseAttributesEnum.CREATIVE],
    fetchByBrand: [ResponseAttributesEnum.PRODUCT, ResponseAttributesEnum.CREATIVE],
    fetchByProduct: [ResponseAttributesEnum.CREATIVE],
  },
};

const CommercialsModal = ({
  isOpen,
  modalTitle,
  onSubmit,
  onCancel,
  commercialsInput,
  commercialsMetadataPromise,
  channel,
  isTimeframeVisible,
  geo,
}) => {
  /* eslint-enable camelcase */
  const SEGMENT_TYPE = 'smartTvCommercials';
  const CHANNELS_WITHOUT_BRAND_PARENT = [CHANNELS.smart_tv_inscape?.name, CHANNELS.hisense?.name];
  const WITH_BRAND_PARENT = !includes(CHANNELS_WITHOUT_BRAND_PARENT, channel);
  const IS_ADVANCED_TV_CHANNEL = includes([CHANNELS.tivo?.name, CHANNELS.hisense?.name], channel);
  const FULL_SCREEN_MAX_HEIGHT = '600px';
  const SMALL_SCREEN_MAX_HEIGHT = '515px';
  const TOOLTIP_MSG = 'Currently streaming data does not have commercials';

  // values currently in dropdown
  const [categoryValues, setCategoryValues] = useState([]);
  const [brandParentValues, setBrandParentValues] = useState([]);
  const [brandValues, setBrandValues] = useState([]);
  const [creativeValues, setCreativeValues] = useState([]);
  const [productsValues, setProductsValues] = useState([]);

  // values selected in dropdown
  const [selectedCategories, setSelectedCategories] = useState(commercialsInput.categories || []);
  const [selectedBrandParents, setSelectedBrandParents] = useState(commercialsInput.brandParents || []);
  const [selectedBrands, setSelectedBrands] = useState(commercialsInput.brands || []);
  const [selectedCreatives, setSelectedCreatives] = useState(commercialsInput.creatives || []);
  const [selectedProducts, setSelectedProducts] = useState(commercialsInput.products || []);

  const geoParam = CHANNELS[channel]?.isGeoMandatory ? geo : '';
  const [showSpinner, setShowSpinner] = useState(true);
  const [modalMaxHeight, setModalMaxHeight] = useState(FULL_SCREEN_MAX_HEIGHT);
  const [selectedStartDate, setSelectedStartDate] = useState(commercialsInput.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(commercialsInput.endDate);
  const [selectedTimeframe, setSelectedTimeframe] = useState(commercialsInput.timeframe);
  const [levelOfExposure, setLevelOfExposure] = useState(commercialsInput.exposure);
  const isHisense = channel === CHANNELS.hisense?.name;
  // France (Hisense ACR) has linear ads only — restrict to Linear like the non-Hisense flow. PHOB-1813.
  const isLinearOnly = !isHisense || isFranceGeo(geo);

  const [viewingType, setViewingType] = useState(
    isLinearOnly ? VIEWING_TYPES_MAP.linear.value : commercialsInput.viewingType || VIEWING_TYPES_MAP.both.value
  );
  const [viewershipModes, setViewershipModes] = useState(commercialsInput.viewModes || []);
  const LIMITED_VIEWING_TYPES = [
    { label: 'Both', value: 'both', disabled: true, tooltip: TOOLTIP_MSG },
    { label: 'Streaming', value: 'streaming', disabled: true, tooltip: TOOLTIP_MSG },
    { label: 'Linear', value: 'linear' },
  ];
  const [isEmptyCommercialInput] = useState(Object.keys(commercialsInput).length === 0);
  const [isEditByKeywords] = useState(
    !isEmptyCommercialInput
      ? commercialsInput.brandParents?.length === 0 &&
          commercialsInput.brands?.length === 0 &&
          commercialsInput.products?.length === 0 &&
          commercialsInput.creatives?.length !== 0
      : false
  );

  const setModalMaxHeights = () => {
    setModalMaxHeight(window.innerHeight > 900 || WITH_BRAND_PARENT ? FULL_SCREEN_MAX_HEIGHT : SMALL_SCREEN_MAX_HEIGHT);
  };

  useEffect(() => {
    setModalMaxHeights();
  });

  const fetchPropsValidation = (res, expectedResponseAttributes) => {
    const missingProperties = difference(expectedResponseAttributes, keys(res));
    if (missingProperties.length)
      throw new Error(`The following properties are missing: ${join(missingProperties, ', ')}`);
  };

  const fetchCommercialsMetadata = async (newViewingType, skipDropdownValues = false) => {
    setShowSpinner(true);
    if (isHisense) {
      commercialsMetadataPromise = getTvCommercialsMetaDataV2(channel, [], [], [], geoParam, newViewingType, []);
    }
    const res = await commercialsMetadataPromise;

    fetchPropsValidation(res, ExpectedResponseAttributes[CHANNELS[channel]?.name].initNew);

    if (res[ResponseAttributesEnum.CATEGORY]) setCategoryValues(res[ResponseAttributesEnum.CATEGORY]);
    if (!skipDropdownValues) {
      if (res[ResponseAttributesEnum.BRAND_PARENT]) setBrandParentValues(res[ResponseAttributesEnum.BRAND_PARENT]);
      if (res[ResponseAttributesEnum.BRAND]) setBrandValues(res[ResponseAttributesEnum.BRAND]);
      if (res[ResponseAttributesEnum.PRODUCT]) setProductsValues(res[ResponseAttributesEnum.PRODUCT]);
    }
    setShowSpinner(false);
  };

  const fetchCommercialsMetadataByParams = (categories, parents, brands, products) => {
    setShowSpinner(true);
    return getTvCommercialsMetaDataV2(
      channel,
      flatMap(parents, 'value'),
      flatMap(brands, 'value'),
      flatMap(products, 'value'),
      geoParam,
      viewingType,
      flatMap(categories, 'value')
    ).finally(() => {
      setShowSpinner(false);
    });
  };

  useEffect(() => {
    const isEditMode = !isEmptyCommercialInput && !isEditByKeywords;
    // A category (hisense) or parent brand scopes the brand/product lists, so when one is selected
    // the dropdown values come from the by-params fetch below and initNew must be skipped. With no
    // such anchor (brands selected directly), initNew returns the full brand universe and must NOT
    // be skipped — otherwise brandValues stays equal to the saved selection and the summary always
    // renders "All brands" instead of the selected brand names.
    const hasAnchorAboveBrands = !isEmpty(selectedCategories) || !isEmpty(selectedBrandParents);
    setBrandParentValues(isEditMode ? selectedBrandParents : []);
    setBrandValues(isEditMode ? selectedBrands : []);
    setProductsValues(isEditMode ? selectedProducts : []);
    setCreativeValues(isEditMode ? selectedCreatives : []);
    const fetchInitialMetadata = async () =>
      await fetchCommercialsMetadata(undefined, isEditMode && hasAnchorAboveBrands);
    fetchInitialMetadata();
    if (isEditMode) {
      // A level was saved as "all" (no exclusion) when its key is absent from queryKeys — see
      // selectQueryKeys. For such levels the saved selection only carried the included subset, so on
      // reopen we re-select the full fetched universe; otherwise the dropdown would render a narrowed
      // list instead of "All X". Levels that were narrowed keep their saved selection.
      const savedQueryKeys = commercialsInput.queryKeys || [];
      const levelHasNoExclusion = (key, excludedKey) =>
        savedQueryKeys.length > 0 && !includes(savedQueryKeys, key) && !includes(savedQueryKeys, excludedKey);
      const applyLevelValues = (res) => {
        if (res[ResponseAttributesEnum.BRAND]) {
          setBrandValues(res[ResponseAttributesEnum.BRAND]);
          if (levelHasNoExclusion('brands', 'excludedBrands')) setSelectedBrands(res[ResponseAttributesEnum.BRAND]);
        }
        if (res[ResponseAttributesEnum.PRODUCT]) {
          setProductsValues(res[ResponseAttributesEnum.PRODUCT]);
          if (levelHasNoExclusion('products', 'excludedProducts'))
            setSelectedProducts(res[ResponseAttributesEnum.PRODUCT]);
        }
        if (res[ResponseAttributesEnum.CREATIVE]) {
          setCreativeValues(res[ResponseAttributesEnum.CREATIVE]);
          if (levelHasNoExclusion('creatives', 'excludedCreatives'))
            setSelectedCreatives(res[ResponseAttributesEnum.CREATIVE]);
        }
      };

      // The by-params fetch below scopes by brand for hisense+category and so omits the brand list;
      // fetch the category's full brand universe separately so an excluded brand level renders the
      // selected brand names rather than "All brands".
      if (isHisense && !isEmpty(selectedCategories)) {
        fetchCommercialsMetadataByParams(selectedCategories, undefined, undefined, undefined).then((res) => {
          if (res[ResponseAttributesEnum.BRAND]) setBrandValues(res[ResponseAttributesEnum.BRAND]);
        });
      }

      fetchCommercialsMetadataByParams(selectedCategories, selectedBrandParents, selectedBrands, selectedProducts).then(
        (res) => {
          let expectedResponseAttributes = [];
          if (selectedBrandParents.length)
            expectedResponseAttributes = ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByParent;
          else if (selectedBrands.length)
            expectedResponseAttributes = ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByBrand;
          else if (selectedProducts.length)
            expectedResponseAttributes = ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByProduct;

          fetchPropsValidation(res, expectedResponseAttributes);
          applyLevelValues(res);
        }
      );
    }
  }, [commercialsMetadataPromise]);

  const setStatesFromFetch = (res, isSelectFetch) => {
    if (res[ResponseAttributesEnum.BRAND]) {
      setBrandValues(res[ResponseAttributesEnum.BRAND]);
      setSelectedBrands(isSelectFetch ? res[ResponseAttributesEnum.BRAND] : []);
    }
    if (res[ResponseAttributesEnum.PRODUCT]) {
      setProductsValues(res[ResponseAttributesEnum.PRODUCT]);
      setSelectedProducts(isSelectFetch ? res[ResponseAttributesEnum.PRODUCT] : []);
    }
    if (res[ResponseAttributesEnum.CREATIVE]) {
      setCreativeValues(res[ResponseAttributesEnum.CREATIVE]);
      setSelectedCreatives(isSelectFetch ? res[ResponseAttributesEnum.CREATIVE] : []);
    }
  };
  const fetchByParent = (isSelectFetch = true) => {
    if (isEmpty(selectedBrandParents)) {
      resetSelection();
      return;
    }
    fetchCommercialsMetadataByParams(undefined, selectedBrandParents, undefined, undefined).then((res) => {
      fetchPropsValidation(res, ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByParent);
      setStatesFromFetch(res, isSelectFetch);
    });
  };

  const fetchByCategory = () => {
    if (isEmpty(selectedCategories)) {
      onResetCategories();
      return;
    }
    setShowSpinner(true);
    const selectedCategoryIds = flatMap(selectedCategories, 'value');
    getTvCommercialsMetaDataV2(channel, [], [], [], geoParam, viewingType, selectedCategoryIds)
      .then((res) => {
        fetchPropsValidation(res, ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByCategory);
        const brands = res[ResponseAttributesEnum.BRAND] || [];
        const products = res[ResponseAttributesEnum.PRODUCT] || [];
        setBrandValues(brands);
        setSelectedBrands(brands);
        setProductsValues(products);
        setSelectedProducts(products);
        if (!isEmpty(brands)) {
          return getTvCommercialsMetaDataV2(
            channel,
            [],
            flatMap(brands, 'value'),
            [],
            geoParam,
            viewingType,
            selectedCategoryIds
          );
        }
        setCreativeValues([]);
        setSelectedCreatives([]);
        return null;
      })
      .then((creativesRes) => {
        if (creativesRes && creativesRes[ResponseAttributesEnum.CREATIVE]) {
          setCreativeValues(creativesRes[ResponseAttributesEnum.CREATIVE]);
          setSelectedCreatives(creativesRes[ResponseAttributesEnum.CREATIVE]);
        }
      })
      .finally(() => {
        setShowSpinner(false);
      });
  };

  const fetchByBrand = (isSelectFetch = true) => {
    if (isEmpty(selectedBrands)) {
      onResetBrands();
      return;
    }
    fetchCommercialsMetadataByParams(undefined, undefined, selectedBrands, undefined).then((res) => {
      fetchPropsValidation(res, ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByBrand);
      setStatesFromFetch(res, isSelectFetch);
    });
  };

  const fetchByProduct = (isSelectFetch = true) => {
    if (isEmpty(selectedProducts)) {
      onResetProducts();
      return;
    }
    fetchCommercialsMetadataByParams(undefined, undefined, undefined, selectedProducts).then((res) => {
      fetchPropsValidation(res, ExpectedResponseAttributes[CHANNELS[channel]?.name].fetchByProduct);
      setStatesFromFetch(res, isSelectFetch);
    });
  };

  const getDropdownSummaryTextBuilder =
    (allSelectedText, noneSelectedText, emptySelectedText = 'Select') =>
    (selectedValues, values) => {
      if (!selectedValues.length) return noneSelectedText;
      if (selectedValues.length === 1 && isEmpty(selectedValues[0])) return emptySelectedText;
      if (selectedValues.length === values.length) return allSelectedText;
      return map(selectedValues, 'label').join(', ');
    };

  const categoriesSummaryTextBuilder = getDropdownSummaryTextBuilder('All categories', 'Select categories');
  const parentsSummaryTextBuilder = getDropdownSummaryTextBuilder('Select parent brands', 'Select parent brands');
  const brandsSummaryTextBuilder = getDropdownSummaryTextBuilder('All brands', 'Select brands', 'Select brands');
  const creativesSummaryTextBuilder = getDropdownSummaryTextBuilder('All creatives', 'Select creatives');
  const productsSummaryTextBuilder = getDropdownSummaryTextBuilder('All products', 'Select products');

  const keywordsSearchDebounce = debounce((searchString, geo, resolve) => {
    const reduceFunction = (param, item) => `${param ? param + ',' : ''}${item.value}`;
    const brand = selectedBrands.reduce(reduceFunction, '');
    const parentBrand = selectedBrandParents.reduce(reduceFunction, '');
    const subCategory = selectedCategories.reduce(reduceFunction, '');
    const paramsObj = { text: searchString };
    if (parentBrand) {
      paramsObj['parent_brand'] = parentBrand;
    }
    if (brand) {
      paramsObj.brand = brand;
    }
    if (subCategory) {
      paramsObj['sub_category'] = subCategory;
    }
    let queryParams = new URLSearchParams(paramsObj).toString();
    queryParams = decodeURIComponent(queryParams);
    getKeywords(channel, queryParams, geoParam).then((res) =>
      resolve(
        res.map((item) => ({
          label: `${item.creative} | ${item.product_name}, ${item.brand_parent}, ${item.brand}`,
          value: item.ad_id,
          product_label: item.product_name,
          brand_label: item.brand,
          brand_parent_label: item.brand_parent,
          secondaryText: `${item.brand_parent} | ${item.brand}`,
        }))
      )
    );
  }, 300);

  const keywordsSearch = (searchString) =>
    new Promise((resolve) => keywordsSearchDebounce(searchString, geoParam, resolve));

  const resetSelection = (newViewingType) => {
    setSelectedCategories([]);
    setSelectedBrandParents([]);
    setSelectedBrands([]);
    setSelectedProducts([]);
    setSelectedCreatives([]);
    fetchCommercialsMetadata(newViewingType);
  };

  const onResetCategories = () => {
    setSelectedCategories([]);
    setSelectedBrands([]);
    setSelectedProducts([]);
    setSelectedCreatives([]);
    fetchCommercialsMetadata(viewingType);
  };

  const onResetBrands = () => {
    setSelectedBrands([]);
    setSelectedProducts([]);
    setSelectedCreatives([]);
    if (WITH_BRAND_PARENT) {
      fetchByParent(false);
    } else if (isHisense && !isEmpty(selectedCategories)) {
      fetchByCategory();
    } else {
      resetSelection();
    }
  };

  const onResetProducts = () => {
    setSelectedProducts([]);
    setSelectedCreatives([]);
    fetchByBrand(false);
  };

  const onChangeViewingType = (newViewingType) => {
    resetSelection(newViewingType);
    setViewingType(newViewingType);
  };

  const onRemove = (item, type) => {
    const resultsWrapper = find(resultsTable, { type });
    const selectedResults = reject(resultsWrapper.results, { value: item.value });
    switch (type) {
      case 'category':
        setSelectedCategories(selectedResults);
        fetchByCategory();
        break;
      case 'parent_brand':
        setSelectedBrandParents(selectedResults);
        fetchByParent(false);
        break;
      case 'brand':
        setSelectedBrands(selectedResults);
        fetchByBrand(false);
        break;
      case 'product':
        setSelectedProducts(selectedResults);
        fetchByProduct(false);
        break;
      case 'creative':
        setSelectedCreatives(selectedResults);
        break;
    }
  };

  const selectQueryKeys = useCallback(() => {
    // Included/excluded ids are an optimization to send the minimal id list per level: send the
    // selected (included) ids when fewer are selected, or the unselected (excluded) ids when more
    // are selected. Returns null only when every value is selected — the anchor above already covers
    // the whole level, so no ids are needed.
    const levelKey = (selected, values, includedKey, excludedKey) => {
      const total = values.length;
      if (!selected.length) return null;
      // Keyword search selects creatives directly (their ad-ids) without populating the level's
      // dropdown universe, so `values` is empty even though specific items are selected. Send the
      // included ids in that case — otherwise the level is dropped from queryKeys and the ad-ids
      // never reach get-segment-ids.
      if (!total) return includedKey;
      if (selected.length === total) return null;
      return selected.length <= total - selected.length ? includedKey : excludedKey;
    };
    const brandsKey = levelKey(selectedBrands, brandValues, 'brands', 'excludedBrands');
    const productsKey = levelKey(selectedProducts, productsValues, 'products', 'excludedProducts');
    const creativesKey = levelKey(selectedCreatives, creativeValues, 'creatives', 'excludedCreatives');

    if (isHisense && !isEmpty(selectedCategories)) return compact(['categories', brandsKey, productsKey, creativesKey]);
    if (WITH_BRAND_PARENT && !isEmpty(selectedBrandParents))
      return compact(['brandParents', brandsKey, productsKey, creativesKey]);
    // Hisense product-led flow: products only become the anchor when the user narrowed products
    // (productsKey === 'products') without narrowing brands. Selecting a brand auto-selects all of
    // its products, so guarding on a real product narrowing prevents that auto-selection from
    // hijacking the anchor and dropping the chosen brand — which would otherwise send product-ids
    // instead of brand-ids. Any other case falls through to the brand-led branch below.
    if (isHisense && !brandsKey && productsKey === 'products') return compact(['products', creativesKey]);

    // Brand-led flow (no category/parent/product anchor): 'brands' carries the selected brand ids
    // and acts as the anchor that scopes the sub-levels. There is no parent to exclude against here,
    // so brands is always sent as an included list. When nothing but creatives is selected (keyword
    // search flow), there is no brand anchor — send 'creatives' (ad-ids) alone rather than an empty
    // 'brands' list, which would otherwise scope the query to no brands.
    if (brandsKey || productsKey || creativesKey)
      return compact([!isEmpty(selectedBrands) && 'brands', productsKey, creativesKey]);
    return ['brandParents'];
  }, [
    selectedCreatives,
    selectedBrands,
    selectedProducts,
    selectedBrandParents,
    brandValues,
    creativeValues,
    productsValues,
    selectedCategories,
    isHisense,
    WITH_BRAND_PARENT,
  ]);

  const resultsTable = useMemo(
    () =>
      compact([
        !isEmpty(selectedCategories) && {
          type: 'category',
          title: 'Category',
          results: selectedCategories,
        },
        !isEmpty(selectedBrandParents) && {
          type: 'parent_brand',
          title: 'Parent brand',
          results: selectedBrandParents,
        },
        !isEmpty(selectedBrands) && {
          type: 'brand',
          title: 'Brand',
          results: selectedBrands,
        },
        !isEmpty(selectedProducts) && {
          type: 'product',
          title: 'Product',
          results: selectedProducts,
        },
        !isEmpty(selectedCreatives) && {
          type: 'creative',
          title: 'Creative',
          results: selectedCreatives,
        },
      ]),
    [selectedCreatives, selectedProducts, selectedBrandParents, selectedBrands, selectedCategories]
  );

  const isHyperLink = (type) => channel === CHANNELS.tivo?.name && type === 'creative';

  const handleSubmit = useCallback(() => {
    const queryKeys = selectQueryKeys();
    // `value` can be an array (brands/products/categories use $addToSet, empty creatives too),
    // so compare by content with isEqual — reference-based differenceBy breaks after a save/re-edit
    // round-trip, where selected items are cloned and no longer share array references with the
    // freshly fetched dropdown values.
    const byValue = (a, b) => isEqual(a.value, b.value);
    const excludedBrands = queryKeys.includes('excludedBrands')
      ? differenceWith(brandValues, selectedBrands, byValue)
      : [];
    const excludedProducts = queryKeys.includes('excludedProducts')
      ? differenceWith(productsValues, selectedProducts, byValue)
      : [];
    const excludedCreatives = queryKeys.includes('excludedCreatives')
      ? differenceWith(creativeValues, selectedCreatives, byValue)
      : [];
    const segment = {
      ...commercialsInput,
      type: SEGMENT_TYPE,
      channel,
      queryKeys,
      ...(isHisense && !isEmpty(selectedCategories) && { categories: selectedCategories }),
      ...(WITH_BRAND_PARENT && { brandParents: selectedBrandParents }),
      brands: selectedBrands,
      ...(IS_ADVANCED_TV_CHANNEL && { products: selectedProducts }),
      creatives: selectedCreatives,
      ...(!isEmpty(excludedBrands) && { excludedBrands }),
      ...(!isEmpty(excludedProducts) && { excludedProducts }),
      ...(!isEmpty(excludedCreatives) && { excludedCreatives }),
      ...(selectedStartDate && { startDate: dateToString(selectedStartDate) }),
      ...(selectedEndDate && { endDate: dateToString(selectedEndDate) }),
      ...(selectedTimeframe && { timeframe: selectedTimeframe }),
      ...(isSmartTvChannel(channel) && { exposure: levelOfExposure }),
    };
    if (!selectedTimeframe) {
      delete segment.timeframe;
    }
    // `...commercialsInput` re-introduces values from a previous save, and the conditional spreads
    // above only add keys — they never remove a stale one. Explicitly drop levels/exclusions that no
    // longer apply, otherwise e.g. deselecting the category leaves its stale `categories` in the
    // segment and the modal reopens as category-led instead of showing the brand selection.
    if (!(isHisense && !isEmpty(selectedCategories))) {
      delete segment.categories;
    }
    if (isEmpty(excludedBrands)) delete segment.excludedBrands;
    if (isEmpty(excludedProducts)) delete segment.excludedProducts;
    if (isEmpty(excludedCreatives)) delete segment.excludedCreatives;

    if (IS_ADVANCED_TV_CHANNEL) {
      segment.viewingType = viewingType;
      segment.viewershipModes = viewershipModes;
    }
    onSubmit(segment);
  }, [
    selectedCategories,
    selectedCreatives,
    selectedBrands,
    selectedBrandParents,
    selectedProducts,
    brandParentValues,
    brandValues,
    creativeValues,
    productsValues,
    selectedStartDate,
    selectedEndDate,
    selectedTimeframe,
    levelOfExposure,
    viewingType,
    viewershipModes,
  ]);

  const handleCancel = () => {
    onCancel(SEGMENT_TYPE);
  };

  return (
    <div className="commercials-modal-component">
      <WindowEventListener events="resize" eventHandlerFunction={setModalMaxHeights} />
      <ModalWithConfirmationButtons
        width="800px"
        maxHeightBeforeScroll={modalMaxHeight}
        modalTitle={modalTitle}
        isOpen={isOpen}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onXClick={handleCancel}
        showSpinner={showSpinner}
        isSubmitDisabled={isEmpty(selectedCreatives) && !(isHisense && !isEmpty(selectedCategories))}
        isDisabledEnterKeyPress={isEmpty(selectedCreatives) && !(isHisense && !isEmpty(selectedCategories))}
        isLightTheme
        isYAutoScrollEnabled={false}
      >
        <div className="commercials-modal-content">
          {IS_ADVANCED_TV_CHANNEL && (
            <div className="modal-row viewer-type-wrapper">
              <div className="viewing-type">
                <div className="row-label">Viewing Type:</div>
                <SingleSelectToggle
                  values={isLinearOnly ? LIMITED_VIEWING_TYPES : VIEWING_TYPES}
                  selectedValue={viewingType}
                  onToggle={onChangeViewingType}
                  isModalTheme
                />
              </div>
              {viewingType != 'streaming' && (
                <div className="viewership-mode">
                  <div className="row-label">Viewership Mode:</div>
                  <Dropdown
                    values={VIEWERSHIP_MODES}
                    selectedValues={viewershipModes}
                    isMulti
                    summaryTextBuilder={getDropdownSummaryTextBuilder('All viewership modes')}
                    onSelect={setViewershipModes}
                  />
                </div>
              )}
            </div>
          )}

          {isHisense && (
            <div className="modal-row categories">
              <DropdownWithHeader
                selectedValues={selectedCategories}
                isMulti
                isSearchable
                summaryTextBuilder={categoriesSummaryTextBuilder}
                onSelect={setSelectedCategories}
                values={categoryValues}
                maxNumberOfSelectedOptions={MAX_SELECTED_MAP.categories}
                reachMaxSelectedOptionsMsg={createLimitMsg('categories')}
                headerTitle="Category:"
                headerResetText="Reset"
                onReset={onResetCategories}
                onClose={fetchByCategory}
              />
            </div>
          )}

          {WITH_BRAND_PARENT && (
            <div className="modal-row parent-brands">
              <DropdownWithHeader
                selectedValues={selectedBrandParents}
                isMulti
                isSearchable
                summaryTextBuilder={parentsSummaryTextBuilder}
                onSelect={setSelectedBrandParents}
                values={brandParentValues}
                maxNumberOfSelectedOptions={MAX_SELECTED_MAP['parent brands']}
                reachMaxSelectedOptionsMsg={createLimitMsg('parent brands')}
                onClose={fetchByParent}
                headerTitle="Parent brand:"
                headerResetText="Reset"
                onReset={resetSelection}
              />
            </div>
          )}
          <div className="modal-row brands">
            <DropdownWithHeader
              selectedValues={selectedBrands}
              isMulti
              isSearchable
              summaryTextBuilder={brandsSummaryTextBuilder}
              onSelect={setSelectedBrands}
              showSelectAllOptions={!isEmpty(selectedBrandParents) || !isEmpty(selectedCategories)}
              values={brandValues}
              isOpen={isEmpty(selectedBrandParents) ? false : undefined}
              onClose={fetchByBrand}
              headerTitle="Brand:"
              headerResetText="Reset"
              onReset={onResetBrands}
            />
          </div>

          {IS_ADVANCED_TV_CHANNEL && (
            <div className="modal-row products">
              <DropdownWithHeader
                selectedValues={selectedProducts}
                isMulti
                isSearchable
                isDisabled={
                  isEmpty(selectedBrands) &&
                  isEmpty(selectedCategories) &&
                  selectedProducts.length > MAX_SELECTED_MAP.products
                }
                summaryTextBuilder={productsSummaryTextBuilder}
                onSelect={setSelectedProducts}
                showSelectAllOptions={!isEmpty(selectedBrands) || !isEmpty(selectedCategories)}
                values={productsValues}
                maxNumberOfSelectedOptions={
                  isEmpty(selectedBrands) && isEmpty(selectedCategories) ? MAX_SELECTED_MAP.products : 0
                }
                reachMaxSelectedOptionsMsg={createLimitMsg('products')}
                isOpen={
                  isEmpty(selectedBrands) || (WITH_BRAND_PARENT && isEmpty(selectedBrandParents)) ? false : undefined
                }
                headerTitle="Product:"
                headerResetText="Reset"
                onReset={onResetProducts}
                onClose={fetchByProduct}
              />
            </div>
          )}

          <div className="modal-row creatives">
            <DropdownWithHeader
              selectedValues={selectedCreatives}
              isMulti
              isSearchable
              isDisabled={isEmpty(selectedBrands) && isEmpty(selectedProducts)}
              summaryTextBuilder={creativesSummaryTextBuilder}
              onSelect={setSelectedCreatives}
              showSelectAllOptions
              values={creativeValues}
              isOpen={
                isEmpty(selectedBrands) || (WITH_BRAND_PARENT && isEmpty(selectedBrandParents)) ? false : undefined
              }
              headerTitle="Creative:"
              headerResetText="Reset"
            />
          </div>

          {IS_ADVANCED_TV_CHANNEL && (
            <div className="modal-row keywords">
              <div className="row-label">Keyword Search:</div>
              <Dropdown
                selectedValues={selectedCreatives}
                customSearch={keywordsSearch}
                isMulti
                isSearchable
                summaryTextBuilder={() => 'Search for Comma-separated Keywords / Phrases'}
                onSelect={setSelectedCreatives}
                reachMaxSelectedOptionsMsg="Sorry, this field is limited to 7 keywords"
              />
            </div>
          )}
          {!isEmpty(resultsTable) && (
            <div className="modal-row selected-items">
              <ResultsTable
                resultsTable={resultsTable}
                onReset={resetSelection}
                onRemove={onRemove}
                splitMainTextByDelimeter={' |'}
                isHyperLink={isHyperLink}
                linkBuilder={creativeVideoLinkBuilder}
              />
            </div>
          )}

          {isTimeframeVisible && isSmartTvChannel(channel) && (
            <div className="modal-row">
              <div className="row-label">Timeframe:</div>
              <TvCustomTimeframe
                startDateString={selectedStartDate}
                endDateString={selectedEndDate}
                timeframe={selectedTimeframe}
                selectedStartDateCB={(selectedDate) => setSelectedStartDate(dateToString(selectedDate))}
                selectedEndDateCB={(selectedDate) => setSelectedEndDate(dateToString(selectedDate))}
                selectedTimeframeCB={setSelectedTimeframe}
              />
            </div>
          )}
          {isSmartTvChannel(channel) && (
            <div className="modal-row level-of-exposure">
              <div className="row-label">
                <span className="label">Level of Exposure:</span>
                <ClickableIcon iconId="info-italic" tooltip={LEVEL_OF_EXPOSURE_COMMERCIALS_TOOLTIP} />
              </div>
              <SelectToggle
                values={LEVELS_OF_EXPOSURE}
                selectedValues={levelOfExposure}
                onChange={setLevelOfExposure}
              />
            </div>
          )}
        </div>
      </ModalWithConfirmationButtons>
    </div>
  );
};

CommercialsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  commercialsMetadataPromise: PropTypes.object,
  commercialsInput: PropTypes.object,
  channel: PropTypes.string,
  isTimeframeVisible: PropTypes.bool,
  geo: PropTypes.string,
};

CommercialsModal.defaultProps = {
  commercialsInput: {},
  channel: '',
  isTimeframeVisible: true,
  geo: '',
};

export default CommercialsModal;
